import { BaseModel, Tag } from "./common.models";
import { ProgramTier } from "./careplan.models";

export enum UserGender {
    Male = 'M',
    Female = 'F',
}

export enum UserLanguage {
    English = 'en-us',
    Hindi = 'hn',
}

export enum FoodHabit {
    Vegetarian = 'Veg',
    Non_Vegetarian = 'Nonveg',
    Eggetarian = 'Eggetarian',
}

export enum MaritalStatus {
    Married = 'Married',
    Single = 'Single',
}

export enum HousingType {
    Family = 'Family',
    Alone = 'Alone',
    Hostel = 'Hostel',
}

export enum DoctorCategory {
    Premium = 'Premium',
    A = 'A',
    B = 'B',
    C = 'C',
    NEW = 'NEW',
}

export enum DoctorSpeciality {
    chat_agent = '0f7e3b8c-4284-4b44-be90-f9e8663eafbe',
    fitness_instructor = '94f41442-a112-47d4-ab1e-6d5b2f78bc2c',
    dietician = 'd828790d-9770-49cf-aba9-4f054bc03d1c',
    health_coach = '48bc4e0b-a70d-491b-bb56-3e8d68d6e2ee',
    gynaecologiest = '66b70ec2-16c3-4163-bca9-1ebdf77c5177',
    dermatologist = '6a48f63d-2e95-48c2-bdb4-b93632fa4031',
    general_surgery = 'a54ddd23-a5d3-4213-83be-8308b062eac8',
}

export interface DietRecallMeal {
    meal_name: string;
    meal_timing: string;
    meal_options: string;
}

export enum SessionNotOptedReason {
    opt_in = 'Opt In For Session',
    pranayam_only = 'pranayam_only',
    prenatal_yoga_allowed = 'prenatal_yoga_allowed',
    complete_bed_rest = 'complete_bed_rest',
    patient_did_not_pay = 'patient_did_not_pay',
    patient_not_interested = 'Patient Not Interested',
    not_permitted_by_doctor = 'Not Permitted By Doctor',
    waiting_for_doctor = "Waiting for Doctor's Confirmation",
    conception_yoga = 'conception_yoga',
    liss = 'liss',
    hiit = 'hiit'
}

export interface PatientProfile {
    id: string;
    name: string;
    dob: string;
    age: number;
    gender: UserGender;
    food_habits: FoodHabit;
    language: UserLanguage;
    suitable_follow_up_time: string;
    preferred_timings: string;
    pregnancy_count: number;
    pregnancy_previous_weight_kg: number;
    delivery_date: string;
    postpartum_delivery_type: string;
    marital_status: MaritalStatus;
    housing_type: HousingType;
    office_end_time: string;
    office_start_time: string;
    phone: string;
    alternate_phone: string;
    primary_cuisine: string;
    secondary_cuisine: string[];
    food_likes: string;
    food_dislikes: string;
    height_cm: number;
    weight_in_kg: number;
    has_miscarriage_history: boolean;
    daily_diet_recall: DietRecallMeal[];
    special_care: boolean;
    session_not_opted_reason: SessionNotOptedReason;
    profession: string;
    working_shift: string;
    work_from_home: boolean;
    override_session_link_access_rule: boolean;
    created: string;
    modified: string;
    who_cooks_food: string;
    energy_levels: string;
    appetite: string;
    comfortable_changing_flour: string;
    is_first_pregnancy: string;
    conditions_in_last_pregnancy: string[];
    timezone: string;
    patient_not_on_whatsapp: boolean;
    chat_team_notes: string,
    instructions_to_chat_team: string,
    fitness_instructor_notes: string,
    first_name: string,
}

export interface UserRole {
    routes: Set<NavBarRoute>;
    default_route: NavBarRoute | AuthRoute;
    module_permissions: Partial<{ [key in UserPermissionModules]: { features: Set<string> } }>;
    inherits_from?: UserRole[];
}

export enum UserPermissionModules {
    /* Users */
    users = 'users',
    /* Careplan */
    prescription = 'prescription',
    enrolment = 'enrolment',
    consultation = 'consultation',
    diet = 'diet',
    lead = 'lead',
    session = 'session',
    /* Messaging */
    channel = 'channel',
    /* Common */
    tags = 'tags',
}

export enum UsersFeatures {
    change_phone_number = 'change_phone_number',
}

export enum PrescriptionFeatures {
    skip_validation = 'skip_validation',
}

export enum EnrolmentFeatures {
    view_all = 'view_all',
    change_owner = 'change_owner',
    change_duration = 'change_duration',
    send_community_link = 'send_community_link',
    view_conversation_starters = 'view_conversation_starters',
    discount_prospect = 'discount_prospect',
    change_phone_number = 'change_phone_number',
    change_instructions_to_chat_team = 'change_instructions_to_chat_team',
    change_chat_team_notes = 'change_chat_team_notes',
    change_fitness_instructor_notes = 'change_fitness_instructor_notes',
    opt_in_to_restricted_batch = 'opt_in_to_restricted_batch',
    view_dietitian_consultation_task = 'view_dietitian_consultation_task',
    view_dietitian_check_in_task = 'view_dietitian_check_in_task',
    view_special_consultation_task = 'view_special_consultation_task',
    view_fitness_instructor_consultation_task = 'view_fitness_instructor_consultation_task',
    view_trial_onboarding_task = 'view_trial_onboarding_task',
    create_complementary_special_consultation_task = 'create_complementary_special_consultation_task',
    view_memory = 'view_memory',
}

export enum LeadFeatures {
    view_restricted_fields = 'view_restricted_fields',
    view_all = 'view_all',
    change_owner = 'change_owner',
    discounted_bill = 'discounted_bill',
    change_phone_number = 'change_phone_number',
    add_qa_comments = 'add_qa_comments',
}

export enum ConsultationFeatures {
    create_custom_event = 'create_custom_event',
    change_owner = 'change_owner',
    change_type = 'change_type',
}

export enum ChannelFeatures {
    send_quick_replies = 'send_quick_replies',
    override_query_validation = 'override_query_validation',
    view_all_queries = 'view_all_queries',
}

export enum TagsFeatures {
    create = 'create',
}

export enum DietFeatures {
    view_all = 'view_all',
    edit_all = 'edit_all',
    approve = 'approve',
}

export enum SessionFeatures {
    view_all = 'view_all',
    edit_all = 'edit_all',
}

export enum NavBarRoute {
    care_dashboard = '/care/dashboard',
    care_tasks = '/care/tasks',
    care_chat = '/care/chat',
    care_patients = '/care/patients',
    care_renewals = '/care/renewals',
    care_patient_assignment = '/care/patient_assignment',
    care_query_assignment = '/care/query_assignment',
    care_diet_chart_template_management = '/care/diet_chart_template_management',
    care_refer_patients = '/care/refer_patients',
    care_whatsapp_chat = '/care/whatsapp_chat',
    doctors_prescribe_patient = '/doctors/prescribe_patient',
    doctors_feedbacks = '/doctors/feedbacks',
    doctors_profile = '/doctors/profile',
    doctors_send_notification = '/doctors/send_notification',
    sessions_calendar = '/sessions/calendar',
    sessions_standalone = '/sessions/standalone',
    sessions_course = '/sessions/course',
    sessions_recurring = '/sessions/recurring',
    sessions_batches = '/sessions/batches',
    sessions_patients = '/sessions/patients',
    sessions_patient_transfer = '/sessions/patient_transfer',
    sessions_tasks = '/sessions/tasks',
    sessions_special_consultation_tasks = '/sessions/special_consultation_tasks',
    sessions_webinars = '/sessions/webinars',
    sales_tasks = '/sales/tasks',
    sales_patients = '/sales/patients',
    sales_chat = '/sales/chat',
    sales_whatsapp_chat = '/sales/whatsapp_chat',
    tools_whatsapp_login = '/tools/whatsapp_login',
    tools_edit_message_template = '/tools/edit_message_template',
    tools_send_message_template = '/tools/send_message_template',
    tools_upload_assets = '/tools/upload_assets',
    tools_knowledge_base = '/tools/knowledge_base',
    tools_query_review = '/tools/query_review',
}

export enum AuthRoute {
    insufficient_access = 'insufficient-access',
    sign_out = 'sign-out',
}

export enum NavBarCategory {
    care = 'care',
    sales = 'sales',
    doctors = 'doctors',
    sessions = 'sessions',
    tools = 'tools',
}

export interface NavBarRouteMetadata {
    name: string;
    route: NavBarRoute;
    icon: string;
    category: NavBarCategory;
    order: number;
}

export interface NavBarCategoryMetadata {
    name: string;
    category: NavBarCategory;
    order: number;
}

export interface DoctorProfile extends BaseModel {
    tags: Tag[];
    phone: string,
    name: string,
    gender: UserGender,
    dob: string,
    city: string,
    district: string,
    registration_no: string,
    experience: number,
    hospital: string,
    consultation_fee: number,
    time_per_patient: number,
    education_description: string,
    speciality: string,
    speciality_name: string,
    photo: string,
    about_me: string,
    profile_links: string,
    google_place_id: string,
    slug: string,
    app_access_state: number,
    preferred_time: string,
    clinic_address: string,
    organisation: string,
    notes_for_careteam: { [key: string]: string },
    user_id: string,
    preferred_careplan_tier: ProgramTier,
    inside_sales_call_sensitivity: "YES" | "NO"
}
